import React, { useState, useEffect, useMemo } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { api } from 'common';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  Chip,
  IconButton,
  Avatar,
  Collapse,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Snackbar,
  Alert,
  Tooltip,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ScheduleIcon from '@mui/icons-material/Schedule';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import PaymentsIcon from '@mui/icons-material/Payments';
import moment from 'moment/min/moment-with-locales';

const BRAND = {
  teal: '#4fd1c5',
  tealDark: '#2dd4bf',
  tealTint: 'rgba(79,209,197,0.10)',
  amber: '#f59e0b',
  amberTint: 'rgba(245,158,11,0.10)',
  green: '#10b981',
  greenTint: 'rgba(16,185,129,0.10)',
  rose: '#f43f5e',
  slate900: '#0f172a',
  slate800: '#1e293b',
  slate700: '#334155',
  slate500: '#64748b',
  slate400: '#94a3b8',
  slate200: '#e2e8f0',
  slate100: '#f1f5f9',
  slate50: '#f8fafc',
  font: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", "Helvetica Neue", sans-serif',
};

const PERIODS = [
  { id: '4w', label: 'Ultime 4 settimane', days: 28 },
  { id: '8w', label: 'Ultime 8 settimane', days: 56 },
  { id: 'month', label: 'Mese corrente', days: null, isCurrentMonth: true },
  { id: 'year', label: 'Anno corrente', days: null, isCurrentYear: true },
  { id: 'all', label: 'Sempre', days: null },
];

const STATUS_FILTERS = [
  { id: 'pending', label: 'Da pagare' },
  { id: 'paid', label: 'Pagati' },
  { id: 'all', label: 'Tutti' },
];

export default function FacilityPayouts() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const auth = useSelector(state => state.auth);
  const bookinglistdata = useSelector(state => state.bookinglistdata);
  const usersdata = useSelector(state => state.usersdata);
  const settings = useSelector(state => state.settingsdata.settings);
  const { updateBooking } = api;

  const [periodId, setPeriodId] = useState('4w');
  const [statusId, setStatusId] = useState('pending');
  const [expanded, setExpanded] = useState({});
  const [actionDialog, setActionDialog] = useState(null);
  const [actionNote, setActionNote] = useState('');
  const [snack, setSnack] = useState(null);

  // Inject Inter font once
  useEffect(() => {
    if (document.querySelector('link[data-medlift-inter]')) return;
    const link = document.createElement('link');
    link.href = 'https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700;800&display=swap';
    link.rel = 'stylesheet';
    link.setAttribute('data-medlift-inter', '1');
    document.head.appendChild(link);
  }, []);

  const driverNameById = useMemo(() => {
    const map = {};
    (usersdata?.staticusers || []).forEach(u => {
      if (u && u.id) map[u.id] = `${u.firstName || ''} ${u.lastName || ''}`.trim();
    });
    return map;
  }, [usersdata?.staticusers]);

  const symbol = settings?.symbol || 'CHF';
  const swipeSymbol = settings?.swipe_symbol;
  const fmtMoney = (n) => {
    const v = (n != null ? Number(n) : 0).toFixed(2);
    return swipeSymbol === false ? `${symbol} ${v}` : `${v} ${symbol}`;
  };

  // Normalize a timestamp value that may be in seconds, ms, or an ISO string
  const normalizeTs = (val) => {
    if (val == null || val === '') return 0;
    if (typeof val === 'number' || /^\d+$/.test(String(val))) {
      const n = Number(val);
      // Heuristic: if value fits in seconds-since-1970 range (< 10^11) treat as seconds.
      // ms timestamps for any sane date are >= 10^12.
      return n < 1e11 ? n * 1000 : n;
    }
    const t = new Date(val).getTime();
    return isNaN(t) ? 0 : t;
  };

  // Filter eligible payouts: completed bookings paid by Struttura (Facility legacy too)
  const filteredBookings = useMemo(() => {
    const myUid = auth?.profile?.uid;
    const all = bookinglistdata?.bookings || [];
    const period = PERIODS.find(p => p.id === periodId);
    const now = Date.now();
    let from = null, to = null;
    if (period?.days) {
      from = now - period.days * 86400000;
    } else if (period?.isCurrentMonth) {
      const d = new Date();
      from = new Date(d.getFullYear(), d.getMonth(), 1).getTime();
    } else if (period?.isCurrentYear) {
      const d = new Date();
      from = new Date(d.getFullYear(), 0, 1).getTime();
    }

    return all.filter(b => {
      if (b.customer !== myUid) return false;
      if (b.status !== 'COMPLETE' && b.status !== 'PAID') return false;
      const paidByStruttura = b.paidBy === 'Struttura' || b.paidBy === 'Facility';
      if (!paidByStruttura) return false;
      const ts = normalizeTs(b.tripdate) || normalizeTs(b.bookingDate);
      if (from && ts && ts < from) return false;
      if (to && ts && ts > to) return false;
      const isPaid = b.facilityPayout === 'PAID';
      if (statusId === 'pending' && isPaid) return false;
      if (statusId === 'paid' && !isPaid) return false;
      return true;
    });
  }, [bookinglistdata?.bookings, auth?.profile?.uid, periodId, statusId]);

  // Group by driver
  const groupedByDriver = useMemo(() => {
    const groups = {};
    filteredBookings.forEach(b => {
      const driverId = b.driver || 'unknown';
      if (!groups[driverId]) {
        groups[driverId] = {
          driverId,
          driverName: b.driver_name || driverNameById[driverId] || 'Trasportatore',
          driverContact: b.driver_contact || '',
          bookings: [],
          totalAmount: 0,
          pendingAmount: 0,
          paidAmount: 0,
          pendingCount: 0,
        };
      }
      const cost = Number(b.trip_cost || 0);
      groups[driverId].bookings.push(b);
      groups[driverId].totalAmount += cost;
      if (b.facilityPayout === 'PAID') {
        groups[driverId].paidAmount += cost;
      } else {
        groups[driverId].pendingAmount += cost;
        groups[driverId].pendingCount += 1;
      }
    });
    return Object.values(groups).sort((a, b) => b.pendingAmount - a.pendingAmount);
  }, [filteredBookings, driverNameById]);

  // KPIs
  const kpi = useMemo(() => {
    let totalDue = 0;
    let totalPaid = 0;
    let driversWithPending = 0;
    let pendingTrips = 0;
    groupedByDriver.forEach(g => {
      totalDue += g.pendingAmount;
      totalPaid += g.paidAmount;
      if (g.pendingAmount > 0) driversWithPending += 1;
      pendingTrips += g.pendingCount;
    });
    return { totalDue, totalPaid, driversWithPending, pendingTrips };
  }, [groupedByDriver]);

  const toggleExpand = (driverId) => {
    setExpanded(prev => ({ ...prev, [driverId]: !prev[driverId] }));
  };

  const openMarkPaidDialog = (target) => {
    setActionDialog(target);
    setActionNote('');
  };

  const closeDialog = () => {
    setActionDialog(null);
    setActionNote('');
  };

  const submitMarkPaid = async () => {
    if (!actionDialog) return;
    const note = actionNote.trim();
    const now = Date.now();

    try {
      if (actionDialog.kind === 'single') {
        const updated = {
          ...actionDialog.booking,
          facilityPayout: 'PAID',
          facilityPayoutDate: now,
          facilityPayoutNote: note || null,
          notifyDriver: true,
          notifyType: 'facility_payout_paid',
        };
        dispatch(updateBooking(updated));
      } else if (actionDialog.kind === 'bulk') {
        actionDialog.bookings.forEach(b => {
          const updated = {
            ...b,
            facilityPayout: 'PAID',
            facilityPayoutDate: now,
            facilityPayoutNote: note || null,
            notifyDriver: true,
            notifyType: 'facility_payout_paid',
          };
          dispatch(updateBooking(updated));
        });
      }
      setSnack({ severity: 'success', message: actionDialog.kind === 'bulk'
        ? `${actionDialog.bookings.length} pagamenti registrati`
        : 'Pagamento registrato' });
    } catch (err) {
      setSnack({ severity: 'error', message: 'Errore durante il salvataggio' });
    }
    closeDialog();
  };

  const exportCsv = () => {
    const rows = [
      ['Trasportatore', 'Riferimento', 'Data trasporto', 'Importo', 'Stato', 'Data pagamento', 'Nota'],
    ];
    groupedByDriver.forEach(g => {
      g.bookings.forEach(b => {
        rows.push([
          g.driverName,
          b.reference || (b.id ? b.id.slice(-6).toUpperCase() : ''),
          b.tripdate ? moment(normalizeTs(b.tripdate)).format('YYYY-MM-DD HH:mm') : '',
          Number(b.trip_cost || 0).toFixed(2),
          b.facilityPayout === 'PAID' ? 'PAGATO' : 'DA PAGARE',
          b.facilityPayoutDate ? moment(b.facilityPayoutDate).format('YYYY-MM-DD') : '',
          b.facilityPayoutNote || '',
        ]);
      });
    });
    const csv = rows.map(r => r.map(cell => {
      const s = String(cell == null ? '' : cell);
      return /[",;\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
    }).join(';')).join('\n');
    const blob = new Blob(['﻿' + csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `pagamenti_trasportatori_${moment().format('YYYY-MM-DD')}.csv`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  };

  // ========== RENDER ==========

  const KpiCard = ({ label, value, icon, color, bg, sub }) => (
    <Box sx={{
      flex: 1,
      minWidth: { xs: '100%', sm: 180 },
      backgroundColor: '#fff',
      borderRadius: '16px',
      border: `1px solid ${BRAND.slate200}`,
      padding: 2.5,
      boxShadow: '0 1px 3px rgba(15,23,42,0.04)',
    }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, mb: 1 }}>
        <Box sx={{ width: 36, height: 36, borderRadius: '10px', backgroundColor: bg, color, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          {icon}
        </Box>
        <Typography sx={{ fontFamily: BRAND.font, fontSize: 11, fontWeight: 700, color: BRAND.slate500, letterSpacing: '0.06em', textTransform: 'uppercase' }}>
          {label}
        </Typography>
      </Box>
      <Typography sx={{ fontFamily: BRAND.font, fontSize: 24, fontWeight: 800, color: BRAND.slate900, letterSpacing: '-0.02em' }}>
        {value}
      </Typography>
      {sub && (
        <Typography sx={{ fontFamily: BRAND.font, fontSize: 12, color: BRAND.slate500, mt: 0.5 }}>
          {sub}
        </Typography>
      )}
    </Box>
  );

  const DriverGroupCard = ({ g }) => {
    const isExpanded = !!expanded[g.driverId];
    const initials = g.driverName.split(' ').slice(0, 2).map(s => s[0] || '').join('').toUpperCase() || 'T';

    return (
      <Box sx={{
        backgroundColor: '#fff',
        borderRadius: '16px',
        border: `1px solid ${BRAND.slate200}`,
        mb: 2,
        boxShadow: '0 1px 3px rgba(15,23,42,0.04)',
        overflow: 'hidden',
      }}>
        <Box
          onClick={() => toggleExpand(g.driverId)}
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 2,
            padding: { xs: 1.75, sm: 2.25 },
            cursor: 'pointer',
            transition: 'background-color 0.15s ease',
            '&:hover': { backgroundColor: BRAND.slate50 },
          }}
        >
          <Avatar sx={{ width: 44, height: 44, backgroundColor: BRAND.tealTint, color: BRAND.tealDark, fontFamily: BRAND.font, fontWeight: 700, fontSize: 14 }}>
            {initials}
          </Avatar>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography sx={{ fontFamily: BRAND.font, fontWeight: 700, fontSize: 15, color: BRAND.slate900 }}>
              {g.driverName}
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.5, alignItems: 'center' }}>
              {g.pendingCount > 0 && (
                <Chip
                  size="small"
                  label={`${g.pendingCount} da pagare`}
                  sx={{ fontFamily: BRAND.font, fontSize: 11, fontWeight: 600, height: 22, color: BRAND.amber, backgroundColor: BRAND.amberTint, border: 'none' }}
                />
              )}
              {g.paidAmount > 0 && (
                <Chip
                  size="small"
                  label={`${g.bookings.length - g.pendingCount} pagati`}
                  sx={{ fontFamily: BRAND.font, fontSize: 11, fontWeight: 600, height: 22, color: BRAND.green, backgroundColor: BRAND.greenTint, border: 'none' }}
                />
              )}
            </Box>
          </Box>
          <Box sx={{ textAlign: 'right' }}>
            <Typography sx={{ fontFamily: BRAND.font, fontSize: 20, fontWeight: 800, color: g.pendingAmount > 0 ? BRAND.amber : BRAND.slate500, letterSpacing: '-0.02em', whiteSpace: 'nowrap' }}>
              {fmtMoney(g.pendingAmount > 0 ? g.pendingAmount : g.paidAmount)}
            </Typography>
            <Typography sx={{ fontFamily: BRAND.font, fontSize: 11, color: BRAND.slate500, mt: 0.25 }}>
              {g.pendingAmount > 0 ? 'da pagare' : 'già saldato'}
            </Typography>
          </Box>
          <IconButton size="small" sx={{
            color: BRAND.slate500,
            transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
            transition: 'transform 0.2s ease',
          }}>
            <ExpandMoreIcon />
          </IconButton>
        </Box>

        <Collapse in={isExpanded}>
          <Box sx={{ borderTop: `1px solid ${BRAND.slate100}`, backgroundColor: BRAND.slate50 }}>
            {g.pendingCount > 1 && (
              <Box sx={{ px: { xs: 1.75, sm: 2.25 }, py: 1.5, borderBottom: `1px solid ${BRAND.slate200}`, display: 'flex', justifyContent: 'flex-end' }}>
                <Button
                  variant="contained"
                  size="small"
                  startIcon={<CheckCircleIcon />}
                  onClick={(e) => {
                    e.stopPropagation();
                    openMarkPaidDialog({
                      kind: 'bulk',
                      title: `Segna come pagati ${g.pendingCount} trasporti per ${g.driverName}`,
                      bookings: g.bookings.filter(b => b.facilityPayout !== 'PAID'),
                      total: g.pendingAmount,
                    });
                  }}
                  sx={{
                    fontFamily: BRAND.font,
                    fontWeight: 700,
                    fontSize: 12,
                    textTransform: 'none',
                    borderRadius: '10px',
                    px: 2,
                    backgroundColor: BRAND.green,
                    boxShadow: 'none',
                    '&:hover': { backgroundColor: '#059669', boxShadow: 'none' },
                  }}
                >
                  Segna tutti pagati ({fmtMoney(g.pendingAmount)})
                </Button>
              </Box>
            )}
            {g.bookings.map(b => {
              const isPaid = b.facilityPayout === 'PAID';
              const ref = b.reference || (b.id ? b.id.slice(-6).toUpperCase() : '');
              return (
                <Box key={b.id} sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1.5,
                  px: { xs: 1.75, sm: 2.25 },
                  py: 1.5,
                  borderBottom: `1px solid ${BRAND.slate100}`,
                  '&:last-child': { borderBottom: 'none' },
                }}>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                      <Typography sx={{ fontFamily: BRAND.font, fontSize: 13, fontWeight: 700, color: BRAND.slate900 }}>
                        #{ref}
                      </Typography>
                      <Typography sx={{ fontFamily: BRAND.font, fontSize: 12, color: BRAND.slate500 }}>
                        {b.tripdate ? moment(normalizeTs(b.tripdate)).format('DD MMM, HH:mm') : '-'}
                      </Typography>
                      <Tooltip title="Apri prenotazione">
                        <IconButton size="small" sx={{ color: BRAND.slate400, p: 0.25 }} onClick={() => navigate(`/bookings/bookingdetails/${b.id}`)}>
                          <OpenInNewIcon sx={{ fontSize: 14 }} />
                        </IconButton>
                      </Tooltip>
                    </Box>
                    {b.pickupAddress && (
                      <Typography sx={{ fontFamily: BRAND.font, fontSize: 11, color: BRAND.slate500, mt: 0.25, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        📍 {b.pickupAddress}
                      </Typography>
                    )}
                    {isPaid && b.facilityPayoutNote && (
                      <Typography sx={{ fontFamily: BRAND.font, fontSize: 11, color: BRAND.slate700, mt: 0.5, fontStyle: 'italic' }}>
                        Nota: {b.facilityPayoutNote}
                      </Typography>
                    )}
                    {isPaid && b.facilityPayoutDate && (
                      <Typography sx={{ fontFamily: BRAND.font, fontSize: 11, color: BRAND.green, mt: 0.25, fontWeight: 600 }}>
                        ✓ Pagato il {moment(b.facilityPayoutDate).format('DD MMM YYYY')}
                      </Typography>
                    )}
                  </Box>
                  <Typography sx={{ fontFamily: BRAND.font, fontSize: 14, fontWeight: 700, color: isPaid ? BRAND.slate500 : BRAND.slate900, whiteSpace: 'nowrap' }}>
                    {fmtMoney(b.trip_cost || 0)}
                  </Typography>
                  {!isPaid ? (
                    <Button
                      variant="outlined"
                      size="small"
                      onClick={() => openMarkPaidDialog({
                        kind: 'single',
                        title: `Segna come pagato ${fmtMoney(b.trip_cost || 0)} a ${g.driverName}`,
                        booking: b,
                        total: Number(b.trip_cost || 0),
                      })}
                      sx={{
                        fontFamily: BRAND.font,
                        fontWeight: 600,
                        fontSize: 11,
                        textTransform: 'none',
                        borderRadius: '8px',
                        py: 0.5,
                        px: 1.25,
                        minWidth: 0,
                        whiteSpace: 'nowrap',
                        borderColor: BRAND.green,
                        color: BRAND.green,
                        '&:hover': { backgroundColor: BRAND.greenTint, borderColor: BRAND.green },
                      }}
                    >
                      Pagato
                    </Button>
                  ) : (
                    <Chip
                      icon={<CheckCircleIcon sx={{ fontSize: 14, color: `${BRAND.green} !important` }} />}
                      label="Saldato"
                      size="small"
                      sx={{ fontFamily: BRAND.font, fontWeight: 600, fontSize: 11, height: 24, backgroundColor: BRAND.greenTint, color: BRAND.green, border: 'none' }}
                    />
                  )}
                </Box>
              );
            })}
          </Box>
        </Collapse>
      </Box>
    );
  };

  return (
    <Box sx={{ p: { xs: 2, md: 3 }, fontFamily: BRAND.font, pb: { xs: 10, md: 3 } }}>
      <Box sx={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1, mb: 2.5 }}>
        <Box>
          <Typography sx={{ fontFamily: BRAND.font, fontWeight: 700, fontSize: 22, color: BRAND.slate900, letterSpacing: '-0.015em' }}>
            Pagamenti Trasportatori
          </Typography>
          <Typography sx={{ fontFamily: BRAND.font, fontSize: 13, color: BRAND.slate500 }}>
            Gestisci i pagamenti dovuti per i trasporti a carico della Struttura
          </Typography>
        </Box>
        <Button
          variant="outlined"
          startIcon={<FileDownloadIcon />}
          onClick={exportCsv}
          disabled={groupedByDriver.length === 0}
          sx={{
            fontFamily: BRAND.font,
            fontWeight: 600,
            fontSize: 13,
            textTransform: 'none',
            borderRadius: '10px',
            borderColor: BRAND.slate200,
            color: BRAND.slate900,
            backgroundColor: '#fff',
            '&:hover': { backgroundColor: BRAND.slate50, borderColor: BRAND.slate300 },
          }}
        >
          Esporta CSV
        </Button>
      </Box>

      {/* KPIs */}
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 3 }}>
        <KpiCard
          label="Da pagare"
          value={fmtMoney(kpi.totalDue)}
          icon={<ScheduleIcon sx={{ fontSize: 20 }} />}
          color={BRAND.amber}
          bg={BRAND.amberTint}
          sub={`${kpi.driversWithPending} ${kpi.driversWithPending === 1 ? 'trasportatore' : 'trasportatori'} • ${kpi.pendingTrips} ${kpi.pendingTrips === 1 ? 'trasporto' : 'trasporti'}`}
        />
        <KpiCard
          label="Già saldato"
          value={fmtMoney(kpi.totalPaid)}
          icon={<CheckCircleIcon sx={{ fontSize: 20 }} />}
          color={BRAND.green}
          bg={BRAND.greenTint}
          sub="nel periodo selezionato"
        />
        <KpiCard
          label="Totale periodo"
          value={fmtMoney(kpi.totalDue + kpi.totalPaid)}
          icon={<PaymentsIcon sx={{ fontSize: 20 }} />}
          color={BRAND.tealDark}
          bg={BRAND.tealTint}
          sub="dovuto + pagato"
        />
      </Box>

      {/* Filters */}
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, alignItems: 'center', mb: 2.5 }}>
        <FormControl size="small" sx={{ minWidth: 180 }}>
          <InputLabel sx={{ fontFamily: BRAND.font, fontSize: 13 }}>Periodo</InputLabel>
          <Select
            label="Periodo"
            value={periodId}
            onChange={(e) => setPeriodId(e.target.value)}
            sx={{ fontFamily: BRAND.font, fontSize: 13, backgroundColor: '#fff', borderRadius: '10px' }}
          >
            {PERIODS.map(p => (
              <MenuItem key={p.id} value={p.id} sx={{ fontFamily: BRAND.font, fontSize: 13 }}>{p.label}</MenuItem>
            ))}
          </Select>
        </FormControl>
        <Box sx={{ display: 'flex', gap: 0.5, backgroundColor: '#fff', border: `1px solid ${BRAND.slate200}`, borderRadius: '10px', p: 0.5 }}>
          {STATUS_FILTERS.map(s => {
            const active = statusId === s.id;
            return (
              <Box
                key={s.id}
                component="button"
                onClick={() => setStatusId(s.id)}
                sx={{
                  fontFamily: BRAND.font,
                  fontSize: 12,
                  fontWeight: 600,
                  border: 'none',
                  cursor: 'pointer',
                  px: 1.5,
                  py: 0.75,
                  borderRadius: '8px',
                  backgroundColor: active ? BRAND.tealTint : 'transparent',
                  color: active ? BRAND.tealDark : BRAND.slate500,
                  '&:hover': { backgroundColor: active ? BRAND.tealTint : BRAND.slate50 },
                }}
              >
                {s.label}
              </Box>
            );
          })}
        </Box>
      </Box>

      {/* List */}
      {groupedByDriver.length === 0 ? (
        <Box sx={{
          p: 6,
          textAlign: 'center',
          backgroundColor: '#fff',
          borderRadius: '16px',
          border: `1px dashed ${BRAND.slate200}`,
        }}>
          <PaymentsIcon sx={{ fontSize: 48, color: BRAND.slate200, mb: 1 }} />
          <Typography sx={{ fontFamily: BRAND.font, fontSize: 15, fontWeight: 600, color: BRAND.slate900 }}>
            Nessun pagamento da mostrare
          </Typography>
          <Typography sx={{ fontFamily: BRAND.font, fontSize: 13, color: BRAND.slate500, mt: 0.5 }}>
            {statusId === 'pending' ? 'Tutti i trasporti del periodo sono già stati saldati.' : 'Nessun trasporto pagato dalla Struttura nel periodo selezionato.'}
          </Typography>
        </Box>
      ) : (
        <Box>
          {groupedByDriver.map(g => <DriverGroupCard key={g.driverId} g={g} />)}
        </Box>
      )}

      {/* Mark paid dialog */}
      <Dialog open={!!actionDialog} onClose={closeDialog} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontFamily: BRAND.font, fontWeight: 700, fontSize: 16, color: BRAND.slate900 }}>
          Conferma pagamento
        </DialogTitle>
        <DialogContent>
          <Typography sx={{ fontFamily: BRAND.font, fontSize: 13, color: BRAND.slate700, mb: 2 }}>
            {actionDialog?.title}
          </Typography>
          {actionDialog?.total && (
            <Box sx={{ p: 2, mb: 2, backgroundColor: BRAND.tealTint, borderRadius: '12px' }}>
              <Typography sx={{ fontFamily: BRAND.font, fontSize: 11, fontWeight: 600, color: BRAND.tealDark, letterSpacing: '0.06em', textTransform: 'uppercase' }}>
                Totale pagamento
              </Typography>
              <Typography sx={{ fontFamily: BRAND.font, fontSize: 22, fontWeight: 800, color: BRAND.slate900, mt: 0.25, letterSpacing: '-0.02em' }}>
                {fmtMoney(actionDialog.total)}
              </Typography>
            </Box>
          )}
          <TextField
            fullWidth
            label="Nota / Riferimento (opzionale)"
            value={actionNote}
            onChange={(e) => setActionNote(e.target.value)}
            placeholder="es. Bonifico Banca XY rif 12345"
            multiline
            rows={2}
            sx={{ fontFamily: BRAND.font, '& input, & textarea': { fontFamily: BRAND.font, fontSize: 13 }, '& label': { fontFamily: BRAND.font, fontSize: 13 } }}
          />
        </DialogContent>
        <DialogActions sx={{ p: 2, pt: 0 }}>
          <Button
            onClick={closeDialog}
            sx={{
              fontFamily: BRAND.font, fontWeight: 600, fontSize: 13, textTransform: 'none',
              color: BRAND.slate500,
              '&:hover': { backgroundColor: BRAND.slate50 },
            }}
          >
            Annulla
          </Button>
          <Button
            variant="contained"
            onClick={submitMarkPaid}
            startIcon={<CheckCircleIcon />}
            sx={{
              fontFamily: BRAND.font, fontWeight: 700, fontSize: 13, textTransform: 'none',
              borderRadius: '10px',
              backgroundColor: BRAND.green,
              boxShadow: 'none',
              '&:hover': { backgroundColor: '#059669', boxShadow: 'none' },
            }}
          >
            Conferma pagamento
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={!!snack}
        autoHideDuration={3500}
        onClose={() => setSnack(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          severity={snack?.severity || 'info'}
          onClose={() => setSnack(null)}
          sx={{ fontFamily: BRAND.font, fontWeight: 600 }}
        >
          {snack?.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
